/**
 * Skeleton loading screen for the account settings page.
 *
 * The body — a page heading followed by one titled block per `accountWidgets`
 * entry — is kept separate from the chrome around it, so that an in-page
 * loading state could reuse it alone. `SkeletonAccountScreen` composes the two,
 * wrapping the body in the nav bar and `StandaloneLayout` that surround the
 * page on a cold load, when no real chrome has loaded yet.
 */

import { createThemedStyles } from '@repo/theme/styles';
import { Pad, PadBox } from 'component/basics';
import { ButtonSkeleton } from 'component/progress/button-skeleton';
import { LoadingAnnouncement } from 'component/progress/loading-announcement';
import { NavBarSkeleton } from 'component/progress/nav-bar-skeleton';
import { TextContainerSkeleton } from 'component/progress/text-container-skeleton';
import { TitledSectionSkeleton } from 'component/progress/titled-section-skeleton';
import { View } from 'react-native';
import { StandaloneLayout } from 'structure/question/standalone-layout';
import { useIsDesktopBreakpoint } from 'util/layout';

// Two widgets are what a regular user sees — request-my-data and
// delete-my-data — the second carrying an action button. The admin-only
// language picker renders nothing for everyone else.
const WIDGET_PLACEHOLDER_COUNT = 2;

function AccountBodySkeleton() {
    return (
        <PadBox horiz={20} vert={20}>
            <TextContainerSkeleton borderRadius={4} height={14} width={110} />
            <Pad size={40} />
            {Array.from({ length: WIDGET_PLACEHOLDER_COUNT }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <View key={i}>
                    {i > 0 && <Pad size={40} />}
                    <TitledSectionSkeleton lines={2} titleWidth={140} />
                </View>
            ))}
            <Pad size={32} />
            <ButtonSkeleton width={115} />
        </PadBox>
    );
}

export function SkeletonAccountScreen() {
    const isDesktop = useIsDesktopBreakpoint();
    const s = useStyles();

    return (
        <View style={s.outer}>
            <LoadingAnnouncement />
            <NavBarSkeleton large={isDesktop} />
            <StandaloneLayout>
                <AccountBodySkeleton />
            </StandaloneLayout>
        </View>
    );
}

const useStyles = createThemedStyles((tokens) => ({
    outer: { flex: 1, backgroundColor: tokens.colors.colorSurfacePrimary },
}));
